/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import {
  SpeechToTextInstance,
  TextToSpeechInstance
} from '../../../../ts/speech';
import {
  createSpeechToTextInstance,
  listSpeechToTextInstances,
  loadSpeechToTextInstance,
  createTextToSpeechInstance,
  listTextToSpeechInstances,
  loadTextToSpeechInstance,
  resetTextToSpeechInstance,
  resetSpeechToTextInstance,
  loadSpeechToTextInstanceInCpd,
  loadTextToSpeechInstanceInCpd,
  listSpeechToTextInstanceUrl,
  listTextToSpeechInstanceUrl,
  loadElevenLabsModels,
  loadElevenLabsVoices
} from './actions';
import {
  speechToTextInstancesAdapter,
  textToSpeechInstancesAdapter
} from './adapters';
// Initial State

const speechToTextInitialState = speechToTextInstancesAdapter.getInitialState({
  listStatus: ApiStatus.IDLE,
  listFetched: null,
  listError: null,
  createStatus: ApiStatus.IDLE,
  createError: null,
  loadStatus: ApiStatus.IDLE,
  loadError: null,
  models: [],
  modelsFetched: null,
  currentInstance: null
});

const textToSpeechInitialState = textToSpeechInstancesAdapter.getInitialState({
  listStatus: ApiStatus.IDLE,
  listFetched: null,
  listError: null,
  createStatus: ApiStatus.IDLE,
  createError: null,
  loadStatus: ApiStatus.IDLE,
  loadError: null,
  voices: [],
  voicesFetched: null,
  currentInstance: null,
  elevenLabsModelsloadStatus: ApiStatus.IDLE,
  elevenLabsModels: [],
  elevenLabsModelsloadError: null
});

// Actual initial State
const initialState = {
  textToSpeech: textToSpeechInitialState,
  speechToText: speechToTextInitialState,
  speechToTextUrl: {
    loadStatus: ApiStatus.IDLE,
    loadError: null,
    value: ''
  },
  textToSpeechUrl: {
    loadStatus: ApiStatus.IDLE,
    loadError: null,
    value: ''
  },
  textToSpeechModelsElevenLabs: {
    models: [],
    loading: ApiStatus.IDLE,
    error: null
  },
  textToSpeechVoicesElevenLabs: {
    voices: [],
    loading: ApiStatus.IDLE,
    error: null
  }
};

/**
 * Gets the initial state of the redux slice and apply updates.
 * Primarily used in unit tests.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = updates => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice + Reducer
const slice = createSlice({
  name: 'speechToText',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // List
    builder
      .addCase(listSpeechToTextInstances.pending, state => {
        state.speechToText.listStatus = ApiStatus.LOADING;
        state.speechToText.listError = null;
      })
      .addCase(listSpeechToTextInstances.fulfilled, (state, action) => {
        state.speechToText.listStatus = ApiStatus.SUCCEEDED;
        state.speechToText.listFetched = Date.now();
        state.speechToText.listError = null;
        speechToTextInstancesAdapter.setAll(
          state.speechToText,
          action.payload as SpeechToTextInstance[]
        );
        if (state.speechToText.currentInstance) {
          // loading the instance may happen before listing all of them.
          // If it is already set, upsert so we have latest data.
          speechToTextInstancesAdapter.upsertOne(
            state.speechToText,
            state.speechToText.currentInstance
          );
        }
      })
      .addCase(listSpeechToTextInstances.rejected, (state, action) => {
        state.speechToText.listStatus = ApiStatus.ERROR;
        state.speechToText.listError = action.error;
      })
      // Create
      .addCase(createSpeechToTextInstance.pending, state => {
        state.speechToText.createStatus = ApiStatus.LOADING;
        state.speechToText.createError = null;
        state.speechToText.currentInstance = null;
      })
      .addCase(createSpeechToTextInstance.fulfilled, state => {
        state.speechToText.createStatus = ApiStatus.SUCCEEDED;
        state.speechToText.createError = null;
      })
      .addCase(createSpeechToTextInstance.rejected, (state, action) => {
        state.speechToText.createStatus = ApiStatus.ERROR;
        state.speechToText.createError =
          // Rejected is due to rejectWithValue and is intentional
          action.error?.message === 'Rejected' ? action.payload : action.error;
      })
      // Load
      .addCase(loadSpeechToTextInstance.pending, (state, _action) => {
        state.speechToText.currentInstance = null;
        state.speechToText.loadStatus = ApiStatus.LOADING;
        state.speechToText.loadError = null;
      })
      .addCase(loadSpeechToTextInstance.fulfilled, (state, action) => {
        if (!state.speechToText.currentInstance) {
          state.speechToText.loadStatus = ApiStatus.SUCCEEDED;
          state.speechToText.loadError = null;
          state.speechToText.models = action.payload.models;
          state.speechToText.modelsFetched = Date.now();
          state.speechToText.currentInstance = action.payload;
        }
        // Make sure we update the existing one we have
        speechToTextInstancesAdapter.upsertOne(
          state.speechToText,
          action.payload
        );
      })
      .addCase(loadSpeechToTextInstance.rejected, (state, action) => {
        state.speechToText.loadStatus = ApiStatus.ERROR;
        state.speechToText.loadError = action.error;
        state.speechToText.currentInstance = null;
      })
      // List
      .addCase(listTextToSpeechInstances.pending, (state, _action) => {
        state.textToSpeech.listStatus = ApiStatus.LOADING;
        state.textToSpeech.listError = null;
      })
      .addCase(listTextToSpeechInstances.fulfilled, (state, action) => {
        state.textToSpeech.listStatus = ApiStatus.SUCCEEDED;
        state.textToSpeech.listFetched = Date.now();
        state.textToSpeech.listError = null;
        textToSpeechInstancesAdapter.setAll(
          state.textToSpeech,
          action.payload as TextToSpeechInstance[]
        );
        if (state.textToSpeech.currentInstance) {
          // loading the instance may happen before listing all of them.
          // If it is already set, upsert so we have latest data.
          textToSpeechInstancesAdapter.upsertOne(
            state.textToSpeech,
            state.textToSpeech.currentInstance
          );
        }
      })
      .addCase(listTextToSpeechInstances.rejected, (state, action) => {
        state.textToSpeech.listStatus = ApiStatus.ERROR;
        state.textToSpeech.listError = action.error;
      })

      // Create
      .addCase(createTextToSpeechInstance.pending, (state, _action) => {
        state.textToSpeech.createStatus = ApiStatus.LOADING;
        state.textToSpeech.createError = null;
        state.textToSpeech.currentInstance = null;
      })
      .addCase(createTextToSpeechInstance.fulfilled, (state, _action) => {
        state.textToSpeech.createStatus = ApiStatus.SUCCEEDED;
        state.textToSpeech.createError = null;
      })
      .addCase(createTextToSpeechInstance.rejected, (state, action) => {
        state.textToSpeech.createStatus = ApiStatus.ERROR;
        state.textToSpeech.createError =
          // Rejected is due to rejectWithValue and is intentional
          action.error?.message === 'Rejected' ? action.payload : action.error;
      })
      // Load
      .addCase(loadTextToSpeechInstance.pending, (state, _action) => {
        state.textToSpeech.currentInstance = null;
        state.textToSpeech.loadStatus = ApiStatus.LOADING;
        state.textToSpeech.loadError = null;
      })
      .addCase(loadTextToSpeechInstance.fulfilled, (state, action) => {
        if (!state.textToSpeech.currentInstance) {
          state.textToSpeech.loadStatus = ApiStatus.SUCCEEDED;
          state.textToSpeech.loadError = null;
          state.textToSpeech.voices = action.payload.voices;
          state.textToSpeech.voicesFetched = Date.now();
          state.textToSpeech.currentInstance = action.payload;
        }
        // Make sure we update the existing one we have
        textToSpeechInstancesAdapter.upsertOne(
          state.textToSpeech,
          action.payload
        );
      })
      .addCase(loadTextToSpeechInstance.rejected, (state, action) => {
        state.textToSpeech.loadStatus = ApiStatus.ERROR;
        state.textToSpeech.loadError = action.error;
        state.textToSpeech.currentInstance = null;
      })
      .addCase(resetTextToSpeechInstance, (state, _action) => {
        state.textToSpeech.createError = null;
        state.textToSpeech.createStatus = ApiStatus.IDLE;
        state.textToSpeech.loadError = null;
        state.textToSpeech.loadStatus = ApiStatus.IDLE;
        state.textToSpeech.currentInstance = null;
        state.textToSpeech.voices = [];
      })
      .addCase(resetSpeechToTextInstance, (state, _action) => {
        state.speechToText.createError = null;
        state.speechToText.createStatus = ApiStatus.IDLE;
        state.speechToText.loadError = null;
        state.speechToText.loadStatus = ApiStatus.IDLE;
        state.speechToText.currentInstance = null;
        state.speechToText.models = [];
      })
      .addCase(loadSpeechToTextInstanceInCpd.pending, (state, _action) => {
        state.speechToText.currentInstance = null;
        state.speechToText.loadStatus = ApiStatus.LOADING;
        state.speechToText.loadError = null;
      })
      .addCase(loadSpeechToTextInstanceInCpd.fulfilled, (state, action) => {
        state.speechToText.loadStatus = ApiStatus.SUCCEEDED;
        state.speechToText.loadError = null;
        state.speechToText.models = action.payload.models;
        state.speechToText.modelsFetched = Date.now();
        state.speechToText.currentInstance = action.payload;
      })
      .addCase(loadSpeechToTextInstanceInCpd.rejected, (state, action) => {
        state.speechToText.loadStatus = ApiStatus.ERROR;
        state.speechToText.loadError = action.error;
        state.speechToText.currentInstance = null;
      })
      .addCase(loadTextToSpeechInstanceInCpd.pending, (state, _action) => {
        state.textToSpeech.currentInstance = null;
        state.textToSpeech.loadStatus = ApiStatus.LOADING;
        state.textToSpeech.loadError = null;
      })
      .addCase(loadTextToSpeechInstanceInCpd.fulfilled, (state, action) => {
        state.textToSpeech.loadStatus = ApiStatus.SUCCEEDED;
        state.textToSpeech.loadError = null;
        state.textToSpeech.voices = action.payload.voices;
        state.textToSpeech.voicesFetched = Date.now();
        state.textToSpeech.currentInstance = action.payload;
      })
      .addCase(loadTextToSpeechInstanceInCpd.rejected, (state, action) => {
        state.textToSpeech.loadStatus = ApiStatus.ERROR;
        state.textToSpeech.loadError = action.error;
        state.textToSpeech.currentInstance = null;
      })
      .addCase(listSpeechToTextInstanceUrl.pending, (state, _action) => {
        state.speechToTextUrl.loadStatus = ApiStatus.LOADING;
        state.speechToTextUrl.loadError = null;
      })
      .addCase(listSpeechToTextInstanceUrl.fulfilled, (state, action) => {
        state.speechToTextUrl.loadStatus = ApiStatus.SUCCEEDED;
        state.speechToTextUrl.loadError = null;
        state.speechToTextUrl.value = action.payload;
      })
      .addCase(listSpeechToTextInstanceUrl.rejected, (state, action) => {
        state.speechToTextUrl.loadStatus = ApiStatus.ERROR;
        state.speechToTextUrl.loadError = action.error;
        state.speechToTextUrl.value = null;
      })
      .addCase(listTextToSpeechInstanceUrl.pending, (state, _action) => {
        state.textToSpeechUrl.loadStatus = ApiStatus.LOADING;
        state.textToSpeechUrl.loadError = null;
      })
      .addCase(listTextToSpeechInstanceUrl.fulfilled, (state, action) => {
        state.textToSpeechUrl.loadStatus = ApiStatus.SUCCEEDED;
        state.textToSpeechUrl.loadError = null;
        state.textToSpeechUrl.value = action.payload;
      })
      .addCase(listTextToSpeechInstanceUrl.rejected, (state, action) => {
        state.textToSpeechUrl.loadStatus = ApiStatus.ERROR;
        state.textToSpeechUrl.loadError = action.error;
        state.textToSpeechUrl.value = null;
      })
      .addCase(loadElevenLabsModels.pending, state => {
        state.textToSpeechModelsElevenLabs.loading = ApiStatus.LOADING;
        state.textToSpeechModelsElevenLabs.error = null;
      })
      .addCase(loadElevenLabsModels.fulfilled, (state, action) => {
        state.textToSpeechModelsElevenLabs.models = action.payload;
        state.textToSpeechModelsElevenLabs.loading = ApiStatus.SUCCEEDED;
        state.textToSpeechModelsElevenLabs.error = null;
      })
      .addCase(loadElevenLabsModels.rejected, (state, action) => {
        state.textToSpeechModelsElevenLabs.loading = ApiStatus.ERROR;
        state.textToSpeechModelsElevenLabs.error = action.payload;
        state.textToSpeechModelsElevenLabs.models = null;
      })
      .addCase(loadElevenLabsVoices.pending, state => {
        state.textToSpeechVoicesElevenLabs.loading = ApiStatus.LOADING;
        state.textToSpeechVoicesElevenLabs.error = null;
      })
      .addCase(loadElevenLabsVoices.fulfilled, (state, action) => {
        state.textToSpeechVoicesElevenLabs.voices = action.payload;
        state.textToSpeechVoicesElevenLabs.loading = ApiStatus.SUCCEEDED;
        state.textToSpeechVoicesElevenLabs.error = null;
      })
      .addCase(loadElevenLabsVoices.rejected, (state, action) => {
        state.textToSpeechVoicesElevenLabs.loading = ApiStatus.ERROR;
        state.textToSpeechVoicesElevenLabs.error = action.error;
        state.textToSpeechVoicesElevenLabs.voices = null;
      });
  }
});

export default slice.reducer;
